/**
 * Side Panel Store
 * Single source of truth for panel visibility, consumed by the app's menus.
 * SidePanel broadcasts canonical panel ids through `panel:visibility-change`
 * window events; this store mirrors them so any component (not just
 * listeners) can read the current state reactively.
 */

import { create } from 'zustand';

interface SidePanelState {
    visiblePanels: Set<string>;
    setPanelVisible: (panelId: string, visible: boolean) => void;
    /** Merge the reported ids into the set. Each mounted SidePanel instance
     * reports its own layout; replacing the set would let the last writer
     * erase the panels reported by the other instances and break the
     * "already visible elsewhere" restore guard. */
    syncVisiblePanels: (panelIds: string[]) => void;
    /** Drop every entry — used when a workspace is applied/reset so the
     * fresh layout re-reports visibility from a clean slate. */
    clearVisiblePanels: () => void;
}

export const useSidePanelStore = create<SidePanelState>((set) => ({
    visiblePanels: new Set<string>(),
    setPanelVisible: (panelId, visible) =>
        set((state) => {
            const next = new Set(state.visiblePanels);
            if (visible) {
                next.add(panelId);
            } else {
                next.delete(panelId);
            }
            return { visiblePanels: next };
        }),
    syncVisiblePanels: (panelIds) =>
        set((state) => {
            const next = new Set(state.visiblePanels);
            panelIds.forEach((id) => next.add(id));
            return { visiblePanels: next };
        }),
    clearVisiblePanels: () => set(() => ({ visiblePanels: new Set<string>() })),
}));
